/**
 * WEB-02 search, filters, sort and pagination: the presentation half. The URL
 * (URLSearchParams) is the single source of truth and round-trips through
 * parseFilters / filtersToParams; searchRequest turns it into the server's
 * Listing.search request, which filters, sorts, pages and counts in the query
 * (rule 7, MERCH-86). Nothing here filters listings after the fetch.
 *
 * Filters are generated from the product type's attributes marked `filter`
 * (B11-P3) and the store's own fields marked filterable (ADR 0009 §4,
 * `type.custom_attributes`, URL name cf_<key>): the URL name (`param`), heading
 * (`filter_label`), value labels (`facet_template`) and ranges (`buckets`) are
 * declared on the attribute, and a type's collections (`?cat=`) on the type.
 * Every function takes the type (a registry entry) as its last argument; without
 * one (`null`, all types) only the common filters apply.
 *
 * Variant options (MERCH-87: sizes, colours…) filter any search: their URL names are
 * opt_<key>, taken from the URL as they come and labelled from the server's facets
 * (`fields` with source "option"); the server ignores a malformed one.
 */
import { customFieldsOf, formatValue, renderTemplate } from '@/lib/productTypes';
import { collectionsOf } from './collections';

export const PAGE_SIZE = 12;

/** [{ param, attr }] for the type's filters: platform attributes, then the store's filterable fields. */
export function attributeFilters(type) {
  if (!type || !Array.isArray(type.attributes)) return [];
  const platform = type.attributes.filter((a) => a.filter).map((a) => ({ param: a.param || a.key, attr: a }));
  const own = customFieldsOf(type).filter((a) => a.filter && a.param).map((a) => ({ param: a.param, attr: a }));
  return [...platform, ...own];
}

/** An option filter's URL name (the server's opt_<key>, lib/catalog/variantSearch). */
export const OPTION_PARAM = /^opt_[a-z0-9_]{1,40}$/;
const keysOf = (input) => (input && typeof input.keys === 'function' && typeof input.get === 'function' ? [...input.keys()] : Object.keys(input || {}));
/** The option filters named in the URL, at most nine (the server's cap). */
const optionParams = (input) => [...new Set(keysOf(input).filter((k) => OPTION_PARAM.test(k)))].slice(0, 9);

/** Multi-value URL params (comma-separated) for a type. */
const multiFor = (type) => ['brand', ...attributeFilters(type).map((x) => x.param), 'grade', 'condition', 'category'];
/** Single-value URL params. */
const SINGLE = ['q', 'cat', 'min', 'max', 'warranty', 'sort', 'page'];

export const WARRANTY_OPTIONS = [
  { value: '', label: 'Any warranty' },
  { value: '3', label: '3 months or more' },
  { value: '6', label: '6 months or more' },
  { value: '12', label: '12 months or more' },
];

export const SORTS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'newest', label: 'Newest first' },
  { value: 'battery', label: 'Best battery health' },
  { value: 'discount', label: 'Biggest discount' },
];
const SORT_KEYS = new Set(SORTS.map((s) => s.value));

// Picks shown beside the results (home, "you might like"): never a filter.
const SORTERS = {
  price_asc: (a, b) => a.price_paise - b.price_paise,
  price_desc: (a, b) => b.price_paise - a.price_paise,
  newest: (a, b) => String(b.published_at || '').localeCompare(String(a.published_at || '')),
  battery: (a, b) => (b.battery_health_pct ?? -1) - (a.battery_health_pct ?? -1),
  discount: (a, b) => discountRatio(b) - discountRatio(a),
};

function discountRatio(l) {
  return l.mrp_paise > l.price_paise ? (l.mrp_paise - l.price_paise) / l.mrp_paise : 0;
}

/** URLSearchParams (or a plain object) → normalised filter object. */
export function parseFilters(input, type) {
  const get = (k) => (input && typeof input.get === 'function' ? input.get(k) : input && input[k]) || '';
  const f = {};
  for (const k of [...multiFor(type), ...optionParams(input)]) f[k] = get(k) ? String(get(k)).split(',').map((s) => s.trim()).filter(Boolean) : [];
  for (const k of SINGLE) f[k] = String(get(k)).trim();
  f.page = Math.max(1, parseInt(f.page, 10) || 1);
  f.sort = SORT_KEYS.has(f.sort) ? f.sort : 'relevance';
  return f;
}

/** Filter object → plain params object with empty values dropped (for setSearchParams). */
export function filtersToParams(f) {
  const out = {};
  for (const k of Object.keys(f)) if (Array.isArray(f[k]) && f[k].length) out[k] = f[k].join(',');
  for (const k of SINGLE) {
    const v = f[k];
    if (v === undefined || v === null || v === '') continue;
    if (k === 'page' && Number(v) <= 1) continue;
    if (k === 'sort' && v === 'relevance') continue;
    out[k] = String(v);
  }
  return out;
}

/** Toggle one value in a multi-value filter; any filter change resets to page 1. */
export function toggleValue(f, key, value) {
  const list = f[key] || [];
  const v = String(value);
  const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
  return { ...f, [key]: next, page: 1 };
}

export function setFilter(f, key, value) {
  return { ...f, [key]: value, page: key === 'page' ? value : 1 };
}

export function clearFilters(f, type) {
  return parseFilters({ q: f.q, sort: f.sort }, type);
}

/** Number of active (non-search, non-sort) filters, for the "Filters (n)" button. */
export function activeFilterCount(f) {
  return Object.values(f).reduce((n, v) => n + (Array.isArray(v) ? v.length : 0), 0) + ['cat', 'min', 'max', 'warranty'].filter((k) => f[k]).length;
}

/**
 * The filter state → the Listing.search request (without page and page_size): the
 * server narrows, sorts and counts. Attribute filters go in `attrs`, keyed by URL name.
 */
export function searchRequest(f, type) {
  const num = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? undefined : Number(v));
  const attrs = Object.fromEntries(attributeFilters(type).filter(({ param }) => (f[param] || []).length).map(({ param }) => [param, f[param]]));
  for (const k of optionParams(f)) if ((f[k] || []).length) attrs[k] = f[k];
  const out = {
    q: f.q || undefined,
    product_type: type ? type.key : undefined,
    cat: type && f.cat ? f.cat : undefined,
    brand: (f.brand || []).length ? f.brand : undefined,
    category_id: (f.category || []).length ? f.category.map(Number).filter(Number.isInteger) : undefined,
    grade: (f.grade || []).length ? f.grade : undefined,
    condition: (f.condition || []).length ? f.condition : undefined,
    // min/max are whole rupees, as in the URL.
    price_min: num(f.min),
    price_max: num(f.max),
    warranty_min: num(f.warranty),
    sort: f.sort && f.sort !== 'relevance' ? f.sort : undefined,
    attrs: Object.keys(attrs).length ? attrs : undefined,
  };
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined));
}

/** Listings in a display order (home picks, "you might like", the Studio preview): never a filter. */
export function sortListings(listings, sort) {
  const fn = SORTERS[sort];
  return fn ? [...(listings || [])].sort(fn) : [...(listings || [])];
}

const titleCase = (v) => String(v).charAt(0).toUpperCase() + String(v).slice(1).replace(/_/g, ' ');

/** One attribute's facet label: its bucket label, its facet template, yes/no, else the formatted value. */
function valueLabel(attr, value) {
  if (attr.buckets) return (attr.buckets.find((b) => b.id === value) || {}).label || value;
  if (attr.facet_template) return renderTemplate(attr.facet_template, { value }) || value;
  if (attr.kind === 'boolean') return value === 'true' ? 'Yes' : 'No';
  return formatValue(attr, value) || value;
}

/** An empty facet set (before the first answer, or a preview with no server). */
export function emptyFacets(type = null, { priceMin = 0, priceMax = 100000 } = {}) {
  return {
    brand: [], grade: [], condition: [], category: [], cat: [], warranty: [],
    collections: collectionsOf(type).map(({ id, label, hint }) => ({ value: id, label, hint })),
    priceMin, priceMax, groups: [],
  };
}

/**
 * The server's facets (Listing.search `facets`: counted in the query over everything on sale of
 * the type, stable as filters change) → the filter panel's options, labelled from the registry.
 * Only values some item in stock has (MERCH-57): a zero-count option is a dead end.
 */
export function facetsFrom(server, type, categories = []) {
  const out = emptyFacets(type);
  if (!server) return out;
  const counts = server.counts || {};
  const opts = (list, label = (v) => v) => (list || []).filter((o) => o.count > 0).map((o) => ({ value: String(o.value), label: label(String(o.value)), count: o.count }));
  out.brand = opts(server.brands);
  out.grade = opts(counts.grade, (v) => `Grade ${v}`);
  out.condition = opts(counts.condition, titleCase);
  out.category = opts(counts.category, (v) => (categories.find((c) => String(c.id) === v) || {}).name || `Category ${v}`);
  const collections = new Map((server.collections || []).map((o) => [o.value, o.count]));
  out.cat = out.collections.filter((c) => collections.get(c.value) > 0).map((c) => ({ value: c.value, label: c.label, count: collections.get(c.value) }));
  const months = counts.warranty_months || [];
  out.warranty = WARRANTY_OPTIONS.filter((o) => o.value)
    .map((o) => ({ ...o, count: months.filter((m) => Number(m.value) >= Number(o.value)).reduce((n, m) => n + m.count, 0) }))
    .filter((o) => o.count > 0);
  const price = server.price || {};
  if (price.min_paise != null) out.priceMin = Math.floor(price.min_paise / 100 / 1000) * 1000;
  if (price.max_paise != null) out.priceMax = Math.ceil(price.max_paise / 100 / 1000) * 1000;
  const values = server.attributes || {};
  for (const { param, attr } of attributeFilters(type)) {
    const list = (values[param] || []).filter((o) => o.count > 0);
    const order = attr.buckets ? (a, b) => attr.buckets.findIndex((x) => x.id === a.value) - attr.buckets.findIndex((x) => x.id === b.value)
      : attr.kind === 'number' ? (a, b) => Number(a.value) - Number(b.value) : (a, b) => a.label.localeCompare(b.label);
    out[param] = list.map((o) => ({ value: String(o.value), label: valueLabel(attr, String(o.value)), count: o.count })).sort(order);
    out.groups.push({ param, label: attr.filter_label || attr.label });
  }
  // Variant options on sale (MERCH-87): labelled as the store spelled them.
  for (const field of (server.fields || []).filter((x) => x && x.source === 'option' && OPTION_PARAM.test(x.param))) {
    const list = (values[field.param] || []).filter((o) => o.count > 0);
    if (!list.length) continue;
    out[field.param] = list.map((o) => ({ value: String(o.value), label: String(o.value), count: o.count }));
    out.groups.push({ param: field.param, label: field.label || field.key });
  }
  return out;
}

/** Removable chips for the active filters: [{ key, value, label }]. */
export function activeChips(f, fc) {
  const chips = [];
  const labelOf = (key, v) => ((fc && fc[key]) || []).find((o) => o.value === v)?.label || v;
  if (f.cat) chips.push({ key: 'cat', value: f.cat, label: ((fc && fc.collections) || []).find((o) => o.value === f.cat)?.label || f.cat });
  for (const k of Object.keys(f)) if (Array.isArray(f[k])) for (const v of f[k]) chips.push({ key: k, value: v, label: labelOf(k, v) });
  if (f.min || f.max) chips.push({ key: 'price', value: '', label: `₹${Number(f.min || 0).toLocaleString('en-IN')} – ${f.max ? `₹${Number(f.max).toLocaleString('en-IN')}` : 'any'}` });
  if (f.warranty) chips.push({ key: 'warranty', value: f.warranty, label: `${f.warranty}+ months warranty` });
  return chips;
}

export function removeChip(f, chip) {
  if (chip.key === 'price') return { ...f, min: '', max: '', page: 1 };
  if (Array.isArray(f[chip.key])) return toggleValue(f, chip.key, chip.value);
  return setFilter(f, chip.key, '');
}
